import {
  Exclude,
  Expose,
  Transform,
  instanceToPlain,
  plainToClassFromExist,
  plainToInstance,
  Type,
} from 'class-transformer';
import type { ClassTransformOptions } from 'class-transformer/types/interfaces';
import * as changeCase from 'change-case';
import type { ColumnSort, PaginationState } from '@tanstack/vue-table';
import {
  AllocationStatus,
  ApPortal,
  InvoiceStatus,
  RemittanceStatus,
  TaskStatus,
  RemittanceSource,
  CreditMemoStatus,
  PaymentApplicationStatus,
  BankStatementStatus,
} from './models';
import dayjs from 'dayjs';
import { TransformStringToDayJS } from './models/utils';

const ToArray = () =>
  Transform(({ value }) =>
    value === undefined ? undefined : Array.isArray(value) ? value : [value],
  );

const ToBool = () =>
  Transform(({ value }) => {
    if (value === 'true' || value === '1' || value === 1) return true;
    if (value === 'false' || value === '0' || value === 0) return false;
    return value; // return original value if it's not 'true' or 'false' (e.g. if it's already a boolean)
  });

export interface SerializeOptions extends ClassTransformOptions {
  forApiRequest?: boolean;
  v2Api?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export abstract class QueryParams {
  @Exclude()
  defaults?: Record<any, any>;

  @Exclude()
  base: any;

  static init<T extends QueryParams>(
    this: new () => T,
    options?: { json?: any; defaults?: Record<any, any> },
  ): T {
    const inst: T = plainToInstance(
      this,
      { ...(options?.defaults || {}), ...(options?.json || {}) },
      {
        exposeDefaultValues: false,
        exposeUnsetFields: true,
        enableImplicitConversion: true,
      },
    );
    inst.base = { ne: this };
    inst.defaults = options?.defaults || {};
    return inst;
  }

  clone<T extends QueryParams>(): T {
    return this.base.ne.init({ defaults: this.defaults });
  }

  serialize(options?: SerializeOptions, includeUnset = false): Record<string, any> {
    options = Object.assign({}, options || {}) as SerializeOptions;
    options.exposeUnsetFields = includeUnset;
    if (options.forApiRequest) {
      if (!options.groups) options.groups = [];
      options.groups = [...options.groups, 'api'];
    }
    const serialized = instanceToPlain(this, options);
    if (options.forApiRequest) {
      return Object.keys(serialized).reduce((result, key) => {
        // Preserve double-underscore keys (v2 filter format like 'assignee_id__in')
        // snakeCase would normalize them to single underscores
        const snakeKey = key.includes('__') ? key : changeCase.snakeCase(key);
        result[snakeKey] = serialized[key];
        return result;
      }, {});
    }

    if (options.v2Api) {
      return Object.keys(serialized).reduce((result, key) => {
        // Preserve double-underscore keys (v2 filter format like 'assignee_id__in')
        // snakeCase would normalize them to single underscores
        const snakeKey = changeCase.camelCase(key.includes('__') ? key.replace('__', '_') : key);
        result[snakeKey] =
          key === 'sortBy' ? changeCase.snakeCase(serialized[key]) : serialized[key];
        return result;
      }, {});
    }

    return serialized;
  }

  update(json: any, clearUnset = true): void {
    plainToClassFromExist(
      this,
      { ...(this.defaults || {}), ...json },
      {
        exposeDefaultValues: false,
        exposeUnsetFields: clearUnset,
        enableImplicitConversion: true,
      },
    );
  }
}

export interface ComparisonFilter {
  gt?: string | null;
  gte?: string | null;
  lt?: string | null;
  lte?: string | null;
}

export type SortOrder = 'asc' | 'desc';

export interface PaginatedQueryState {
  pagination?: PaginationState;
  sort: ColumnSort[];
}

export class PaginatedQueryParams extends QueryParams {
  sortBy?: string | undefined;

  sortOrder?: SortOrder | undefined;

  @Exclude({ toPlainOnly: true })
  pageSize: number = 50;

  @Exclude()
  _page: number = 0;

  @Exclude()
  _key: number = 0;

  @Expose({ groups: ['api'] })
  get limit(): number {
    return this.pageSize;
  }

  @Expose()
  get page(): number {
    return this._page;
  }

  set page(p: number | string | undefined) {
    if (p !== undefined) {
      try {
        this._page = parseInt(p.toString());
      } catch {
        this._page = 0;
      }
    }
  }

  set state(state: { pagination?: PaginationState; sort?: ColumnSort[] }) {
    const update = {
      sortBy: state.sort?.length ? state.sort[0].id : undefined,
      sortOrder: state.sort?.length ? (state.sort[0].desc ? 'desc' : 'asc') : undefined,
    };
    if (state.pagination) {
      Object.assign(update, {
        page: state.pagination.pageIndex,
        pageSize: state.pagination.pageSize,
      });
    }

    // Explicitly clear sort fields when sort is empty, since
    // this.update with clearUnset=false won't clear undefined values
    if (!state.sort?.length) {
      this.sortBy = undefined;
      this.sortOrder = undefined;
    }

    this.update(update, false);
  }

  get paginationState(): PaginationState {
    return {
      pageIndex: this.page || 0,
      pageSize: this.pageSize,
    };
  }

  set paginationState(state: PaginationState) {
    this.update(
      {
        page: state.pageIndex,
        pageSize: state.pageSize,
      },
      false,
    );
  }

  get sortState(): ColumnSort[] {
    if (this.sortBy) {
      return [{ id: this.sortBy, desc: this.sortOrder === 'desc' }];
    }
    return [];
  }

  set sortState(sortState: ColumnSort[] | undefined) {
    this.update(
      {
        page: 0,
        sortBy: sortState?.length ? sortState[0].id : undefined,
        sortOrder: sortState?.length ? (sortState[0].desc ? 'desc' : 'asc') : undefined,
      },
      false,
    );
  }
}

export class ApPortalSessions extends PaginatedQueryParams {
  ap_portal: ApPortal;
  exclude_empty?: string;
  status?: string[];
  search?: string;
  partner?: string;
}

export class ApPortalQuery extends PaginatedQueryParams {
  ap_portal_account_id?: string;
}

export class APIRequestLogQueryParams extends PaginatedQueryParams {
  linkedIntegration?: string;

  @Exclude({ toPlainOnly: true })
  pageSize: number = 20;
}

export class InvoiceQueryParams extends PaginatedQueryParams {
  remoteCreatedAt: ComparisonFilter | undefined;
  istatus?: InvoiceStatus[];
  search: string | undefined;
  partner: string | undefined;
  assignee_id: string | undefined | null;
  issued_at?: ComparisonFilter;

  @ToBool()
  has_outreach?: boolean;
  @ToBool()
  customer_outreach_paused?: boolean;
  @ToBool()
  in_dispute?: boolean;
  currency?: string[];
  customer?: string[];
  business_unit_id__in?: string[];

  // Custom field filters: object in-memory, JSON string on the wire / in URL.
  @Transform(({ value }) => (value && typeof value === 'object' ? JSON.stringify(value) : value), {
    toPlainOnly: true,
  })
  @Transform(
    ({ value }) => {
      if (typeof value === 'string') {
        try {
          return JSON.parse(value);
        } catch {
          return undefined;
        }
      }
      return value;
    },
    { toClassOnly: true },
  )
  custom_fields?: Record<string, Record<string, any>>;
}

export class CreditMemoQueryParams extends PaginatedQueryParams {
  status?: CreditMemoStatus[];
  search?: string;
  partner?: string;
  posted_date?: ComparisonFilter;
  business_unit_id__in?: string[];
}

export class CustomerTransactionQueryParams extends PaginatedQueryParams {
  // Unified ledger filters for GET /v2/partners/{partner_id}/transactions.
  // Values are the unified vocab: type invoice|payment|credit_memo,
  // status draft|open|complete|void. serialize({ v2Api: true }) maps these to
  // the SDK's camelCase query params (e.g. type__in -> typeIn).
  @ToArray()
  type__in?: string[];

  @ToArray()
  status__in?: string[];

  @ToArray()
  currency__in?: string[];

  date__gte?: string;

  date__lte?: string;

  // Free-text name search.
  search?: string;
}

export class EmailTemplateQueryParams extends PaginatedQueryParams {
  search: string | undefined;
  folder: string | undefined;

  @ToBool()
  cash_app?: boolean;
}

export class BankTransactionQueryParams extends PaginatedQueryParams {
  search: string | undefined;
  date_from: string | undefined;
  date_to: string | undefined;
  bank_account_id: string | undefined;
  amount_min: number | undefined;
  amount_max: number | undefined;
  currency: string | undefined;

  @ToArray()
  status__in: PaymentApplicationStatus[] | undefined;

  @ToArray()
  allocation_status__in: AllocationStatus[] | undefined;

  @ToBool()
  include_withdrawals: boolean | undefined;

  @ToBool()
  has_posting_errors: boolean | undefined;

  approved_by: string | undefined;

  partner_id: string | undefined;
}

export class BankStatementQueryParams extends PaginatedQueryParams {
  date?: string;

  search?: string | undefined;

  account_id?: string;

  original_statement_id?: string;

  @ToArray()
  status__in?: BankStatementStatus[];
}

export class CallQueryParams extends PaginatedQueryParams {
  direction: string | undefined;
  partner: string | undefined;
  invoice: string | undefined;
  workflow: string | undefined;
  classification: string[] | undefined;
  @ToArray()
  sentiment: string[] | undefined;
  business_unit_id__in?: string[];

  @TransformStringToDayJS()
  before?: dayjs.Dayjs;

  @TransformStringToDayJS()
  after?: dayjs.Dayjs;
}

export class SMSThreadQueryParams extends PaginatedQueryParams {
  search?: string | undefined;
  partner?: string | undefined;
  business_unit_id__in?: string[];

  @TransformStringToDayJS()
  before?: dayjs.Dayjs;

  @TransformStringToDayJS()
  after?: dayjs.Dayjs;
}

export class SmsTemplateQueryParams extends PaginatedQueryParams {
  search?: string | undefined;
}

export class PartnerQueryParams extends PaginatedQueryParams {
  search: string | undefined;
  delimited_search: string | undefined;

  @ToBool()
  has_outreach?: boolean;

  @ToBool()
  outreach_paused?: boolean;
  group_id?: string;

  // FilterBuilder-style filters (v2 API double-underscore format)
  assignee_id__in?: string[];
  assignee_id__nin?: string[];
  billing_address__country__in?: string[];
  invoice_balance__gte?: number;
  invoice_balance__lte?: number;
  num_open_invoices__gte?: number;
  num_open_invoices__lte?: number;
  dunning_state_id__in?: string[];
  dunning_state_id__nin?: string[];
  business_unit_id__in?: string[];
  segment_id?: string;

  // Custom field filters: object in-memory, JSON string on the wire / in URL.
  @Transform(({ value }) => (value && typeof value === 'object' ? JSON.stringify(value) : value), {
    toPlainOnly: true,
  })
  @Transform(
    ({ value }) => {
      if (typeof value === 'string') {
        try {
          return JSON.parse(value);
        } catch {
          return undefined;
        }
      }
      return value;
    },
    { toClassOnly: true },
  )
  custom_fields?: Record<string, Record<string, any>>;
}

export class EmailThreadQueryParams extends PaginatedQueryParams {
  search: string | undefined;
  partner: string | undefined;
  invoice: string | undefined;
  workflow: string | undefined;
  assignee: string | undefined;
  triage_status?: string;
  is_from_centralized_inbox?: boolean;
  category?: string;
  has_responses: string | undefined;
  has_open_tasks: string | undefined;
  business_unit_id__in?: string[];
  /** API: boolean. Inbox route/query: UI slugs (`read_only` | `unread_only` | `all_messages`). */
  read?: boolean | string;

  /** API: boolean. Inbox route/query: `'true'` string when set, otherwise omitted. */
  @ToBool()
  is_internal?: boolean | string;
}

export class EmailQueryParams extends PaginatedQueryParams {
  partner?: string;
  invoice?: string;
}

export type PaymentQueryStageParam = 'needs-review' | 'unallocated' | 'finalized';

export class PaymentQueryParams extends PaginatedQueryParams {
  date?: string;

  partner?: string;

  bank_account?: string;

  search?: string;

  stage?: PaymentQueryStageParam;

  business_unit_id__in?: string[];

  @ToBool()
  include_total_allocated?: boolean;
}

export class RemittanceQueryParams extends PaginatedQueryParams {
  date?: string;

  partner?: string;

  status?: RemittanceStatus;

  source_type?: RemittanceSource;

  search?: string;
}

export class TaskQueryParams extends PaginatedQueryParams {
  status: TaskStatus[] | undefined;
  assignee: string | undefined;
  dispute: string | undefined;
  classifications_any: string[] | undefined;
  classifications_eq: string[] | undefined;
  partner: string | undefined;
  is_automated: string | undefined;

  business_unit_id__in?: string[];
  time_range?: string;

  @ToArray()
  task_type?: string[] | undefined;

  @ToArray()
  exclude_type?: string[] | undefined;

  @ToArray()
  expected_action: string[] | undefined;

  @ToBool()
  is_subtask_view?: boolean;

  @Exclude({ toPlainOnly: true })
  group_view_mode?: string;
}

export class TaskGroupByQueryParams extends PaginatedQueryParams {
  status: TaskStatus[] | undefined;
  group_by: string;
  assignee: string | undefined;
}

export class TaskCollectionQueryParams extends PaginatedQueryParams {
  status: TaskStatus[] | undefined;
  assignee: string | undefined;

  group_view_mode?: string;
  group_view_id?: string;

  collection_type?: string;
  collection_id?: string;

  sub_collection_type?: string;
  sub_collection_id?: string;

  task_drawer_id?: string;
}

export class MyTasksQueryParams extends PaginatedQueryParams {
  view_type: string;
  group_by?: string;
}

export class ClaimQueryParams extends PaginatedQueryParams {
  created_at_gte?: string | undefined;
  created_at_lte?: string | undefined;
  customer: string[] | undefined;

  @ToBool()
  approved?: boolean;

  @ToBool()
  matched?: boolean;
  document?: string;
  search?: string;
}

export class ConversationQueryParams extends PaginatedQueryParams {
  dispute?: string;
}

export class DisputeQueryParams extends PaginatedQueryParams {
  assignee?: string;
  partner?: string;
  invoice?: string;
  priority?: string;
  reason?: string;
  search?: string;

  @ToBool()
  is_ready_to_resolve?: boolean;
}

export class DisputeReasonQueryParams extends PaginatedQueryParams {}

export class DisputeDocumentQueryParams extends PaginatedQueryParams {}

export class DeductionQueryParams extends PaginatedQueryParams {
  created_at_gte?: string | undefined;
  created_at_lte?: string | undefined;
  effective_payment_date_gte?: string | undefined;
  effective_payment_date_lte?: string | undefined;
  effective_issue_date_gte?: string | undefined;
  effective_issue_date_lte?: string | undefined;
  customer: string[] | undefined;
  @ToBool()
  approved?: boolean;
  @ToBool()
  matched?: boolean;
  @ToBool()
  pre_deduction?: boolean;
  @ToBool()
  has_new_changes?: boolean;
  @ToBool()
  has_alerts?: boolean;
  @ToBool()
  is_new?: boolean;
  document?: string;
  status?: string | string[] | null;
  search?: string;
  po_number?: string;
  approval_status?: 'approved' | 'rejected' | 'pending';
}

export class DeductionDocumentQueryParams extends PaginatedQueryParams {
  customer: string[] | undefined;
  search?: string;
  source?: string | undefined;
  type?: string | undefined;
  status?: string | undefined;
  receivedAtGte?: string | undefined;
  receivedAtLte?: string | undefined;
  createdAtGte?: string | undefined;
  createdAtLte?: string | undefined;
  @ToBool()
  missingCustomer?: boolean | undefined;
  apPortalSessionId?: string | undefined;
}

export class ExportResponseQueryParams extends PaginatedQueryParams {}

export class NotificationQueryParams extends PaginatedQueryParams {
  search?: string;
}

export class PartnerStageEvalQueryParams extends PaginatedQueryParams {
  workflow: string | undefined;
  stage_id: string | undefined;
  date: string | undefined;
  status: string | undefined;
}

export class AuditLogQueryParams extends PaginatedQueryParams {
  start_date?: string;
  end_date?: string;
  actor_id?: string;
  module?: 'setup' | 'collections' | 'cash_app' | 'deductions';
}
